import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { deepEquals } from '../utils/objectUtilities';
import useCredentials from './credentialsHook';
import { useUpsertSetting, useSetting, useSettingsLoading } from './settingsHooks';

export function useSettingState(key, initialValue, {
    // If true, local value always syncs with server updates
    // If false, local value only syncs if it hasn't been locally modified
    followServer = true,
    // If true, local changes are persisted to server
    persistChanges = true
} = {}) {
    const upsertSetting = useUpsertSetting();
    const credentials = useCredentials();
    const setting = useSetting(key);
    const loading = useSettingsLoading();
    const [localValue, setLocalValue] = useState(setting === undefined ? initialValue : setting?.value);
    const hasLoadedOnce = useRef(!loading);
    const prevPersistChanges = useRef(persistChanges);

    // handle loading - we don't keep any changes until loading is complete
    useEffect(() => {
        if (!loading && !hasLoadedOnce.current) {
            hasLoadedOnce.current = true;
            setLocalValue(setting === undefined ? initialValue : setting?.value);
        }
    }, [setting, loading, initialValue]);

    // Sync local changes when persistChanges becomes true
    useEffect(() => {
        if (hasLoadedOnce.current && 
            persistChanges && 
            !prevPersistChanges.current && 
            !deepEquals(localValue, setting?.value)) {
                // persistChanges just became true and we have unsaved local changes
                upsertSetting(credentials.username, key, localValue);
        }
        prevPersistChanges.current = persistChanges;
    }, [persistChanges, localValue, setting?.value, credentials.username, key, upsertSetting]);
    
    // sync changes from server is followServer is true
    useEffect(() => {
        setLocalValue(prev => {
            if (hasLoadedOnce.current && followServer) {
                if (!deepEquals(prev, setting?.value)) {
                    return setting?.value;
                }
            }
            return prev;
        });
    }, [setting, followServer, loading]);

    const setValue = useCallback(value => {
        if (hasLoadedOnce.current) {
            setLocalValue(prev => {
                const next = typeof value === 'function' ? value(prev) : value;
                if (!deepEquals(prev, next)) {
                    if (hasLoadedOnce.current && persistChanges) {
                        upsertSetting(credentials.username, key, next);
                    }
                }
                return next;
            });
        }
    }, [credentials.username, key, upsertSetting, persistChanges]);

    // Optional: reset function to sync with server
    const resetToServer = useCallback(() => {
        setLocalValue(setting?.value === undefined ? initialValue : setting?.value);
    }, [setting?.value, initialValue]);

    return useMemo(() => [localValue, setValue, { resetToServer }], [localValue, setValue, resetToServer]);
}
